import moment from 'moment';

import { Component, Input, input, OnInit, output } from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, filter, startWith } from 'rxjs/operators';

import {
  AddressFormConfig,
  ContactPropertySearcherType,
  Language,
  NameValue,
  Profile
} from '@ui/shared/models';
import {
  AddressFormComponent,
  AppInputDirective,
  BaseStep,
  DateComponent,
  DropdownSelectComponent,
  FormFieldComponent,
  FormFieldLabelComponent,
  HintComponent,
  InfoBoxComponent,
  RegexTypes,
  SelectComponent
} from '@ui/legacy-lib';
import { combineLatest } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-internal-list-member-basic-information',
  templateUrl: './member-basic-information.component.html',
  styleUrls: ['./member-basic-information.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FormFieldLabelComponent,
    FormFieldComponent,
    SelectComponent,
    InfoBoxComponent,
    DateComponent,
    AppInputDirective,
    DropdownSelectComponent,
    HintComponent,
    AddressFormComponent
  ]
})
export class MemberBasicInformationComponent
  extends BaseStep
  implements OnInit
{
  private _housingPermissionTypes: NameValue<string>[];
  readonly form = input<FormGroup>(undefined);
  readonly hasOfflineUserAccess = input<boolean>(undefined);
  readonly isNewMember = input<boolean>(undefined);
  readonly offlineContactExists = input<boolean>(undefined);
  readonly emailContactExists = input<boolean>(undefined);
  readonly isSelfRegistration = input<boolean>(undefined);
  readonly contactType = input<ContactPropertySearcherType>(undefined);
  readonly isExistingOnlineContact = input<boolean>(undefined);
  readonly availableLanguages = input<Language[]>(undefined);
  readonly hasCooperativeModule = input<boolean>(undefined);

  @Input()
  get housingPermissionTypes() {
    return this._housingPermissionTypes;
  }

  set housingPermissionTypes(value) {
    this._housingPermissionTypes = [
      this.housingPermissionDefaultType,
      ...(value || [])
    ];
  }

  readonly checkOfflineUserExists =
    output<Pick<Profile, 'firstname' | 'name'>>();
  readonly checkEmailUserExists = output<Pick<Profile, 'email'>>();

  public memberTypes = [
    {
      name: 'general.member_type_offline_l',
      value: ContactPropertySearcherType.OFFLINE
    },
    {
      name: 'general.member_type_online_l',
      value: ContactPropertySearcherType.ONLINE
    }
  ];

  public startDateStruct: NgbDateStruct = {
    year: 1990,
    month: 1,
    day: 1
  };

  public minDateStruct: NgbDateStruct = {
    year: 1900,
    month: 1,
    day: 1
  };

  public maxDateStruct: NgbDateStruct = {
    year: moment().year(),
    month: moment().month() + 1,
    day: moment().date()
  };

  private housingPermissionDefaultType: NameValue<string> = {
    value: null,
    name: 'general.housing_permission_not_available_l'
  };

  public addressConfig: AddressFormConfig = {
    showCountry: true
  };

  public get minDate() {
    const now = moment();

    return {
      year: now.year(),
      month: now.month() + 1,
      day: now.date()
    };
  }

  public get addressGroup() {
    return this.form().get('address') as FormGroup;
  }

  public get housingPermissionTypeControl() {
    return this.form().get('housingPermission.type');
  }

  private get housingPermissionAmountPeopleControl() {
    return this.form().get('housingPermission.amountPeople');
  }

  public get isOfflineUser() {
    return this.typeControl.value === ContactPropertySearcherType.OFFLINE;
  }

  private get typeControl() {
    return this.form().get('propertySearcherType');
  }

  private get phoneControl() {
    return this.form().get('data.phone');
  }

  private get emailControl() {
    return this.form().get('email');
  }

  private get birthdayControl() {
    return this.form().get('data.birthday');
  }

  private get firstNameControl() {
    return this.form().get('data.firstname');
  }

  private get nameControl() {
    return this.form().get('data.name');
  }

  ngOnInit(): void {
    this.form().enable();
    const isNewMember = this.isNewMember();
    if (isNewMember || !this.isExistingOnlineContact()) {
      this.typeControl.valueChanges
        .pipe(startWith(this.typeControl.value), untilDestroyed(this))
        .subscribe(type => {
          if (type === ContactPropertySearcherType.OFFLINE) {
            this.disableControlsForOfflineContact();
            this.phoneControl.setValidators(
              Validators.compose([
                Validators.required,
                Validators.pattern(RegexTypes.PHONE)
              ])
            );
          } else {
            this.emailControl.enable();
            this.birthdayControl.enable();
            this.emailControl.setValidators(Validators.email);
            this.phoneControl.clearValidators();
          }
          this.phoneControl.updateValueAndValidity();
        });

      combineLatest([
        this.firstNameControl.valueChanges,
        this.nameControl.valueChanges,
        this.typeControl.valueChanges
      ])
        .pipe(
          debounceTime(500),
          filter(
            ([firstname, name, type]) =>
              type === ContactPropertySearcherType.OFFLINE &&
              !!firstname &&
              !!name
          ),
          untilDestroyed(this)
        )
        .subscribe(([firstname, name]) =>
          this.checkOfflineUserExists.emit({ firstname, name })
        );
      this.emailControl.valueChanges
        .pipe(debounceTime(500), untilDestroyed(this))
        .subscribe(email => this.checkEmailUserExists.emit({ email }));
    }

    this.housingPermissionTypeControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (!value) {
          this.housingPermissionAmountPeopleControl.reset();
        }
      });

    if (!isNewMember && this.isOfflineUser) {
      this.disableControlsForOfflineContact();
    }
    super.ngOnInit();
  }

  private disableControlsForOfflineContact(): void {
    this.emailControl.disable();
    this.birthdayControl.disable();
  }
}
